import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  TRAILING_NARROW_DISTANCE_PERCENT,
  resolveTrailingSettings,
  tpSlTriggerPrice,
  trailingStopPrice,
  trailingTakeProfitError,
} from "@crypto-trading/common/browser";

const inputClass =
  "w-full bg-primary/40 border border-gray-600 text-text-primary rounded-lg p-2 text-sm focus:outline-none focus:border-accent";

const formatPrice = (price: number): string => {
  if (price >= 1) {
    return price.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: price >= 1000 ? 2 : 4,
    });
  }
  return Number(price.toPrecision(4)).toString();
};

const toNumber = (value: string): number | undefined =>
  value.trim() === "" ? undefined : Number(value);

/** Error for the trailing inputs, or null. Empty fields use the defaults. */
export const trailingInputsError = (
  activationStr: string,
  distanceStr: string
): string | null =>
  trailingTakeProfitError(toNumber(activationStr), toNumber(distanceStr));

/** API payload fields for the take-profit section. */
export const takeProfitPayload = (
  trailing: boolean,
  takeProfitStr: string,
  activationStr: string,
  distanceStr: string
) =>
  trailing
    ? {
        takeProfitPercent: undefined,
        trailingTakeProfit: true,
        trailingActivationPercent: toNumber(activationStr),
        trailingDistancePercent: toNumber(distanceStr),
      }
    : {
        takeProfitPercent: takeProfitStr ? Number(takeProfitStr) : undefined,
        trailingTakeProfit: false,
      };

interface ModeSwitchProps {
  trailing: boolean;
  onChange: (trailing: boolean) => void;
}

/** Fixed / Trailing switch shown in the take-profit box header. */
export const TakeProfitModeSwitch: React.FC<ModeSwitchProps> = ({
  trailing,
  onChange,
}) => (
  <div
    role="radiogroup"
    aria-label="Take profit type"
    className="flex gap-1 rounded-lg bg-black/30 p-0.5"
  >
    {[
      { label: "Fixed", value: false },
      { label: "Trailing stop", value: true },
    ].map((option) => (
      <button
        key={option.label}
        type="button"
        role="radio"
        aria-checked={trailing === option.value}
        onClick={() => onChange(option.value)}
        className={`flex-1 rounded-md px-2 py-1 text-[11px] font-semibold transition-colors ${
          trailing === option.value
            ? "bg-white text-gray-900"
            : "text-text-secondary hover:text-text-primary"
        }`}
      >
        {option.label}
      </button>
    ))}
  </div>
);

interface InputsProps {
  activation: string;
  distance: string;
  onActivationChange: (value: string) => void;
  onDistanceChange: (value: string) => void;
  /** Current price, for the "starts at" hint. */
  entryPrice?: number | null;
  positionType: "LONG" | "SHORT";
}

/** The two trailing settings, in place of the take-profit % input. */
export const TrailingTakeProfitInputs: React.FC<InputsProps> = ({
  activation,
  distance,
  onActivationChange,
  onDistanceChange,
  entryPrice,
  positionType,
}) => {
  const settings = resolveTrailingSettings(
    toNumber(activation),
    toNumber(distance)
  );
  const error = trailingInputsError(activation, distance);
  const startsAt =
    entryPrice && entryPrice > 0
      ? tpSlTriggerPrice(
          entryPrice,
          settings.activationPercent,
          positionType,
          "tp"
        ) ?? entryPrice
      : null;
  const narrow =
    error == null &&
    settings.distancePercent < TRAILING_NARROW_DISTANCE_PERCENT;
  const long = positionType === "LONG";

  return (
    <div className="space-y-2">
      <label className="block space-y-1">
        <span className="text-[11px] text-text-secondary">
          Start after a {long ? "rise" : "fall"} of %
        </span>
        <input
          type="number"
          min={0}
          max={1000}
          step="1"
          inputMode="decimal"
          className={inputClass}
          value={activation}
          onChange={(event) => onActivationChange(event.target.value)}
          placeholder={String(settings.activationPercent)}
          aria-label="Start trailing after a price move of, percent"
        />
      </label>
      <label className="block space-y-1">
        <span className="text-[11px] text-text-secondary">
          Close after a {long ? "drop from the high" : "rise from the low"} of %
        </span>
        <input
          type="number"
          min={1}
          max={90}
          step="1"
          inputMode="decimal"
          className={inputClass}
          value={distance}
          onChange={(event) => onDistanceChange(event.target.value)}
          placeholder={String(settings.distancePercent)}
          aria-label="Close after the price moves back from its best level by, percent"
        />
      </label>
      {error ? (
        <p className="text-[11px] font-medium text-red-400">{error}</p>
      ) : startsAt != null ? (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1.5 text-emerald-300">
          <p className="text-[10px] uppercase tracking-wide opacity-80">
            Trailing starts at
          </p>
          <p className="font-mono text-sm font-semibold tabular-nums">
            ${formatPrice(startsAt)}
          </p>
        </div>
      ) : null}
      {narrow && (
        <p className="text-[11px] leading-snug text-amber-300">
          Under {TRAILING_NARROW_DISTANCE_PERCENT}% often closes on a normal
          pullback. In past listings that did worse than a fixed take profit.
        </p>
      )}
    </div>
  );
};

interface ExplainerProps {
  activation: string;
  distance: string;
  positionType: "LONG" | "SHORT";
}

/** "How it works", collapsible, full width below the TP/SL boxes. */
export const TrailingTakeProfitExplainer: React.FC<ExplainerProps> = ({
  activation,
  distance,
  positionType,
}) => {
  const [open, setOpen] = useState(false);
  const settings = resolveTrailingSettings(
    toNumber(activation),
    toNumber(distance)
  );
  const { activationPercent: a, distancePercent: d } = settings;
  const long = positionType === "LONG";
  // Worked example from $1.00: the price runs twice the activation move
  // (at least 50% for a long, 30% for a short) before turning.
  const entry = 1;
  const startsAt = long ? entry * (1 + a / 100) : entry * (1 - a / 100);
  const best = long
    ? entry * (1 + Math.max(2 * a, 50) / 100)
    : entry * (1 - Math.min(90, Math.max(2 * a, 30)) / 100);
  const closeAt = trailingStopPrice(entry, best, positionType, settings);
  const resultPercent =
    closeAt != null
      ? long
        ? (closeAt / entry - 1) * 100
        : (1 - closeAt / entry) * 100
      : null;
  const money = (n: number) => `$${n.toFixed(2)}`;

  return (
    <div className="rounded-xl border border-gray-500/20 bg-black/30">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-text-primary"
      >
        How the trailing stop loss works
        <ChevronDown
          className={`h-4 w-4 text-text-secondary transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div className="space-y-2.5 px-4 pb-4 text-xs leading-relaxed text-text-secondary">
          <p>
            No take-profit order is placed on the exchange. Instead, Trigger
            follows the live price while the position is open.
          </p>
          <ol className="list-decimal space-y-1.5 pl-4">
            <li>
              When the price has {long ? "risen" : "fallen"} {a}% from your
              entry, trailing starts and Trigger remembers the{" "}
              {long ? "highest" : "lowest"} price from then on.
            </li>
            <li>
              If the price then {long ? "drops" : "rises"} {d}% from that{" "}
              {long ? "high" : "low"}, Trigger closes the position at market.
            </li>
            <li>
              Until trailing starts, only your stop loss protects the
              position. It stays on the exchange the whole time.
            </li>
          </ol>
          {closeAt != null && resultPercent != null && (
            <p className="rounded-lg bg-white/5 px-3 py-2 text-text-primary">
              Example: you enter at {money(entry)}. Trailing starts at{" "}
              {money(startsAt)}. If the price {long ? "peaks" : "bottoms"} at{" "}
              {money(best)}, the position closes when it reaches{" "}
              {money(closeAt)}, a {resultPercent >= 0 ? "gain" : "loss"} of{" "}
              {Math.abs(resultPercent).toFixed(0)}% in price.
            </p>
          )}
          <p>
            Why the defaults are wide: new listings often pull back 10–20%
            and then climb again. In a test on a year of past listings, tight
            trails closed on those pullbacks and did worse than a fixed take
            profit. Starting after +40% and closing on a 30% drop did about
            as well as a fixed 50% take profit and kept more of the big runs.
          </p>
          <p>
            The close is a market order sent right after the drop, so in a
            very fast move the exit price can be worse than the line. Auto-close
            and your stop loss still apply.
          </p>
        </div>
      )}
    </div>
  );
};
